import {
  KeyOutlined,
  LoadingOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Alert, Empty, Input, Skeleton, Tooltip } from 'antd';
import type { InputRef } from 'antd';
import { AxiosError } from 'axios';
import { AnimatePresence, motion } from 'motion/react';
import {
  MouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import InfraGraphIcon from 'images/infra-graph-icon.svg?react';
import {
  focusCliSession,
  openCliSession,
  setCliPicking,
} from './cliSessionStore';
import { useAccess } from 'hooks/useAccess';
import SecretInput from 'components/ui/SecretInput/SecretInput';
import { isClusterAsleep } from 'helpers/clusterStatus';
import {
  useAuthenticateCliCluster,
  useCliAuthorizedClusters,
} from 'hooks/cliTerminalQueries';
import { clusterInfraPath } from 'utils/clusterPaths';
import { centreOf, startPageTransition } from 'utils/pageTransition';

interface CliSessionChooserProps {
  sessions: string[];
  authFor: string | null;
  roomy?: boolean;
}

const ROW_MOTION = {
  type: 'spring' as const,
  stiffness: 520,
  damping: 42,
  mass: 0.7,
};
const SECTION_MOTION = {
  type: 'spring' as const,
  stiffness: 380,
  damping: 36,
  mass: 0.9,
};

const remainingHours = (expiresAt: string) => {
  const hours = (new Date(expiresAt).getTime() - Date.now()) / 3_600_000;
  if (!Number.isFinite(hours) || hours <= 0) return 'expired';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m left`;
  return `${Math.round(hours)}h left`;
};

const statusLabel = (isOpen: boolean, isAsleep: boolean) => {
  if (isOpen) return 'Session open';
  if (isAsleep) return 'Asleep';
  return 'Open terminal';
};

const warmMap = () =>
  Promise.all([
    import('components/InfraMap/InfraMap'),
    import('components/InfraMap/graph/ClusterGraph'),
  ]);

function CliSessionChooser({
  sessions,
  authFor,
  roomy = false,
}: Readonly<CliSessionChooserProps>) {
  const { can } = useAccess();
  const navigate = useNavigate();
  const location = useLocation();
  const { sessions: authorized, isPending } = useCliAuthorizedClusters();
  const {
    authenticate,
    isPending: isAuthenticating,
    reset,
  } = useAuthenticateCliCluster();

  const [filter, setFilter] = useState('');
  const [token, setToken] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const tokenRef = useRef<InputRef>(null);
  const canOpenMap = can('infrastructure.read');

  useEffect(() => {
    if (authFor) setTimeout(() => tokenRef.current?.focus(), 120);
  }, [authFor]);

  const needle = filter.trim().toLowerCase();
  const ready = useMemo(
    () =>
      authorized
        .filter(session => session.cluster.toLowerCase().includes(needle))
        .sort((a, b) => a.cluster.localeCompare(b.cluster)),
    [authorized, needle],
  );

  const open = (name: string) => {
    if (sessions.includes(name)) focusCliSession(name);
    else openCliSession(name);
    setCliPicking(false);
  };

  const openMap = (event: MouseEvent<HTMLAnchorElement>, name: string) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    event.preventDefault();
    const origin = centreOf(event.currentTarget);
    setCliPicking(false);
    warmMap()
      .catch(() => undefined)
      .then(() =>
        startPageTransition({
          kind: 'open-map',
          origin,
          update: () =>
            navigate(clusterInfraPath(name), {
              state: { from: `${location.pathname}${location.search}` },
            }),
        }),
      );
  };

  const submit = async () => {
    const pasted = token.trim();
    if (!pasted) {
      setProblem('Paste the access token the cluster owner generated for you.');
      return;
    }

    setProblem(null);
    setNotice(null);
    reset();
    try {
      // Opened for a cluster, the token must be that cluster's; otherwise the token names its cluster.
      const { data } = await authenticate({
        instanceName: authFor,
        clusterAccessToken: pasted,
      });
      setToken('');
      if (isClusterAsleep(data.status)) {
        setNotice(
          `${data.cluster} is unlocked but asleep. Start it to open a terminal.`,
        );
        return;
      }
      open(data.cluster);
    } catch (error) {
      const failure = error as AxiosError<{
        code?: string;
        error?: string;
        message?: string;
      }>;
      if (authFor && failure.response?.data?.code === 'CLI_TOKEN_REJECTED') {
        setProblem(`Wrong token. It was not generated for ${authFor}.`);
        return;
      }
      setProblem(
        failure.response?.data?.error ??
          failure.response?.data?.message ??
          failure.message ??
          'That token was not accepted.',
      );
    }
  };

  const renderReady = () => {
    if (isPending) return <Skeleton active paragraph={{ rows: 3 }} />;
    if (!ready.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            needle
              ? 'No unlocked cluster matches that'
              : 'No cluster unlocked yet. Paste a token below to open your first terminal.'
          }
        />
      );
    }

    return (
      <AnimatePresence initial={false} mode="popLayout">
        {ready.map(session => {
          const isOpen = sessions.includes(session.cluster);
          const isAsleep = !isOpen && isClusterAsleep(session.status);
          return (
            <motion.div
              key={session.cluster}
              layout
              className={`CliSessionPicker__row${isOpen ? ' is-open' : ''}${isAsleep ? ' is-asleep' : ''}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={ROW_MOTION}
            >
              <button
                type="button"
                className="CliSessionPicker__open"
                disabled={isAsleep}
                onClick={() => open(session.cluster)}
              >
                <span className="CliSessionPicker__name">
                  {session.cluster}
                </span>
                <Tooltip
                  title={`Access expires ${new Date(session.expiresAt).toLocaleString()}`}
                >
                  <span className="CliSessionPicker__expiry">
                    {remainingHours(session.expiresAt)}
                  </span>
                </Tooltip>
                <span className="CliSessionPicker__status">
                  {statusLabel(isOpen, isAsleep)}
                </span>
              </button>
              {canOpenMap && (
                <a
                  className="CliSessionPicker__map"
                  href={clusterInfraPath(session.cluster)}
                  onPointerEnter={() => warmMap().catch(() => undefined)}
                  onClick={event => openMap(event, session.cluster)}
                >
                  <InfraGraphIcon />
                  View map
                </a>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    );
  };

  return (
    <div
      className={`CliSessionChooser${roomy ? ' CliSessionChooser--roomy' : ''}`}
    >
      <div className="CliSessionPicker__section">
        <div className="CliSessionPicker__section-head">
          <h4>Unlocked clusters</h4>
          <Input
            allowClear
            autoComplete="off"
            size="small"
            prefix={<SearchOutlined />}
            placeholder="Filter"
            value={filter}
            onChange={event => setFilter(event.target.value)}
            className="CliSessionPicker__filter"
          />
        </div>
        <div className="CliSessionPicker__list">{renderReady()}</div>
      </div>

      <motion.div
        className="CliSessionPicker__section CliSessionPicker__section--unlock"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SECTION_MOTION, delay: 0.06 }}
      >
        <div className="CliSessionPicker__section-head">
          <h4>Unlock with an access token</h4>
        </div>

        <div className="CliSessionPicker__unlock">
          {authFor && (
            <Input
              disabled
              size="small"
              value={authFor}
              aria-label="Cluster"
              className="CliSessionPicker__cluster"
            />
          )}
          <SecretInput
            ref={tokenRef}
            size={roomy ? 'middle' : 'small'}
            value={token}
            placeholder="Paste the cluster access token"
            prefix={<KeyOutlined />}
            onChange={event => {
              setToken(event.target.value);
              setProblem(null);
              setNotice(null);
            }}
            onPressEnter={submit}
            className="CliSessionPicker__token"
          />
          <motion.button
            type="button"
            className="CliSessionPicker__unlock-action"
            disabled={isAuthenticating}
            whileTap={isAuthenticating ? undefined : { scale: 0.97 }}
            transition={ROW_MOTION}
            onClick={submit}
          >
            {isAuthenticating && <LoadingOutlined />}
            {isAuthenticating ? 'Checking' : 'Unlock'}
          </motion.button>
        </div>

        <AnimatePresence initial={false}>
          {problem ? (
            <motion.div
              key="problem"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={SECTION_MOTION}
              className="CliSessionPicker__problem"
            >
              <Alert type="error" showIcon message={problem} />
            </motion.div>
          ) : null}
          {notice ? (
            <motion.div
              key="notice"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={SECTION_MOTION}
              className="CliSessionPicker__problem"
            >
              <Alert type="info" showIcon message={notice} />
            </motion.div>
          ) : null}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

export default CliSessionChooser;
